import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminLabs from "./pages/admin/Labs";
import AdminClients from "./pages/admin/Clients";
import AdminAlerts from "./pages/admin/Alerts";
import AdminSettings from "./pages/admin/Settings";
import AdminAuditLogs from "./pages/admin/AuditLogs";
import AdminDailyReports from "./pages/admin/DailyReports";
import AdminExports from "./pages/admin/Exports";
import ClientChat from "./pages/client/Chat";
import LabDashboard from "./pages/lab/LabDashboard";
import LabChat from "./pages/lab/LabChat";
import OrdersList from "./modules/orders/OrdersList";
import OrderNew from "./modules/orders/OrderNew";
import OrderDetail from "./modules/orders/OrderDetail";
import AdminOrdersInbox from "./modules/orders/AdminOrdersInbox";
import LabOrdersList from "./modules/orders/LabOrdersList";
import LabInvoices from "./modules/invoicing/LabInvoices";
import AdminInvoicing from "./modules/invoicing/AdminInvoicing";
import AdminPricing from "./modules/invoicing/AdminPricing";
import CabinetInvoices from "./modules/invoicing/CabinetInvoices";
import AdminCockpit from "./modules/cockpit/AdminCockpit";
import AdminAnalytics from "./modules/analytics/AdminAnalytics";
import AdminExecutive from "./modules/analytics/AdminExecutive";
import AdminSearch from "./modules/analytics/AdminSearch";
import RegisterPage from "./modules/registration/RegisterPage";
import AdminRegistrations from "./modules/registration/AdminRegistrations";
import AdminRetention from "./modules/retention/AdminRetention";
import AdminPartners from "./modules/partners/AdminPartners";
import AdminBackups from "./modules/backups/AdminBackups";
import AdminEmailInbox from "./modules/email_gateway/AdminEmailInbox";
import CabinetSearch from "./modules/orders/CabinetSearch";
import LegalPage from "./pages/LegalPage";
import HelpPage from "./pages/HelpPage";

const getRedirectPath = (role) => {
  if (role === 'admin') return '/admin';
  if (role === 'lab') return '/lab';
  return '/orders';
};

// Role-array constants — defined once so React doesn't recreate an array on
// every render of the parent (avoids needless `<ProtectedRoute>` re-renders).
const ROLES_ADMIN  = ['admin'];
const ROLES_CLIENT = ['client'];
const ROLES_LAB    = ['lab'];

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center"><div className="animate-pulse text-muted-foreground">Chargement...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to={getRedirectPath(user.role)} replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center"><div className="animate-pulse text-muted-foreground">Chargement...</div></div>;
  if (user) return <Navigate to={getRedirectPath(user.role)} replace />;
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />

      {/* Pages publiques : légal + aide */}
      <Route path="/mentions-legales" element={<LegalPage />} />
      <Route path="/cgu" element={<LegalPage />} />
      <Route path="/confidentialite" element={<LegalPage />} />
      <Route path="/aide" element={<HelpPage />} />

      {/* Admin routes */}
      <Route path="/admin" element={<ProtectedRoute allowedRoles={ROLES_ADMIN}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/orders" element={<ProtectedRoute allowedRoles={ROLES_ADMIN}><AdminOrdersInbox /></ProtectedRoute>} />
      <Route path="/admin/cockpit" element={<ProtectedRoute allowedRoles={ROLES_ADMIN}><AdminCockpit /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute allowedRoles={ROLES_ADMIN}><AdminAnalytics /></ProtectedRoute>} />
      <Route path="/admin/executive" element={<ProtectedRoute allowedRoles={ROLES_ADMIN}><AdminExecutive /></ProtectedRoute>} />
      <Route path="/admin/search" element={<ProtectedRoute allowedRoles={ROLES_ADMIN}><AdminSearch /></ProtectedRoute>} />
      <Route path="/admin/registrations" element={<ProtectedRoute allowedRoles={ROLES_ADMIN}><AdminRegistrations /></ProtectedRoute>} />
      <Route path="/admin/retention" element={<ProtectedRoute allowedRoles={ROLES_ADMIN}><AdminRetention /></ProtectedRoute>} />
      <Route path="/admin/backups" element={<ProtectedRoute allowedRoles={ROLES_ADMIN}><AdminBackups /></ProtectedRoute>} />
      <Route path="/admin/email-inbox" element={<ProtectedRoute allowedRoles={ROLES_ADMIN}><AdminEmailInbox /></ProtectedRoute>} />
      <Route path="/admin/partners" element={<ProtectedRoute allowedRoles={ROLES_ADMIN}><AdminPartners /></ProtectedRoute>} />
      <Route path="/admin/orders/:orderId" element={<ProtectedRoute allowedRoles={ROLES_ADMIN}><OrderDetail /></ProtectedRoute>} />
      <Route path="/admin/invoicing" element={<ProtectedRoute allowedRoles={ROLES_ADMIN}><AdminInvoicing /></ProtectedRoute>} />
      <Route path="/admin/pricing" element={<ProtectedRoute allowedRoles={ROLES_ADMIN}><AdminPricing /></ProtectedRoute>} />
      <Route path="/admin/labs" element={<ProtectedRoute allowedRoles={ROLES_ADMIN}><AdminLabs /></ProtectedRoute>} />
      <Route path="/admin/clients" element={<ProtectedRoute allowedRoles={ROLES_ADMIN}><AdminClients /></ProtectedRoute>} />
      <Route path="/admin/alerts" element={<ProtectedRoute allowedRoles={ROLES_ADMIN}><AdminAlerts /></ProtectedRoute>} />
      <Route path="/admin/audit" element={<ProtectedRoute allowedRoles={ROLES_ADMIN}><AdminAuditLogs /></ProtectedRoute>} />
      <Route path="/admin/reports" element={<ProtectedRoute allowedRoles={ROLES_ADMIN}><AdminDailyReports /></ProtectedRoute>} />
      <Route path="/admin/exports" element={<ProtectedRoute allowedRoles={ROLES_ADMIN}><AdminExports /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute allowedRoles={ROLES_ADMIN}><AdminSettings /></ProtectedRoute>} />

      {/* Client routes */}
      <Route path="/chat" element={<ProtectedRoute allowedRoles={ROLES_CLIENT}><ClientChat /></ProtectedRoute>} />
      <Route path="/orders" element={<ProtectedRoute allowedRoles={ROLES_CLIENT}><OrdersList /></ProtectedRoute>} />
      <Route path="/orders/new" element={<ProtectedRoute allowedRoles={ROLES_CLIENT}><OrderNew /></ProtectedRoute>} />
      <Route path="/orders/:orderId" element={<ProtectedRoute allowedRoles={ROLES_CLIENT}><OrderDetail /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute allowedRoles={ROLES_CLIENT}><CabinetInvoices /></ProtectedRoute>} />
      <Route path="/search" element={<ProtectedRoute allowedRoles={ROLES_CLIENT}><CabinetSearch /></ProtectedRoute>} />

      {/* Lab routes */}
      <Route path="/lab" element={<ProtectedRoute allowedRoles={ROLES_LAB}><LabDashboard /></ProtectedRoute>} />
      <Route path="/lab/orders" element={<ProtectedRoute allowedRoles={ROLES_LAB}><LabOrdersList /></ProtectedRoute>} />
      <Route path="/lab/orders/:orderId" element={<ProtectedRoute allowedRoles={ROLES_LAB}><OrderDetail /></ProtectedRoute>} />
      <Route path="/lab/invoices" element={<ProtectedRoute allowedRoles={ROLES_LAB}><LabInvoices /></ProtectedRoute>} />
      <Route path="/lab/chat/:channelId" element={<ProtectedRoute allowedRoles={ROLES_LAB}><LabChat /></ProtectedRoute>} />

      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
